import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, CheckCircle2, XCircle, Clock, Filter } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeavePage = ({ user, setUser }) => {
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  // Leave-row pending cancellation (drives the confirm AlertDialog so we
  // never use the suppressed-in-preview ``window.confirm``).
  const [leaveToCancel, setLeaveToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Filter states for Leave History
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [leaveStatusFilter, setLeaveStatusFilter] = useState('all');

  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'AL',
    leave_category: 'full_day',
    from_date: '',
    to_date: '',
    reason: ''
  });

  const calculatedDays = useMemo(() => {
    if (!leaveForm.from_date || !leaveForm.to_date) return null;
    const from = new Date(leaveForm.from_date);
    const to = new Date(leaveForm.to_date);
    if (to < from) return null;
    const diffDays = Math.floor((to - from) / (1000 * 60 * 60 * 24)) + 1;
    return leaveForm.leave_category === 'half_day' ? diffDays * 0.5 : diffDays;
  }, [leaveForm.from_date, leaveForm.to_date, leaveForm.leave_category]);

  // Pending days (not yet approved/rejected) per leave type for the current year.
  // Used to warn employees who try to apply for leaves they've already exhausted
  // (counting approved + pending) and to block the submit button.
  const pendingByType = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const acc = { AL: 0, OH: 0, RH: 0 };
    (leaves || []).forEach((l) => {
      if (l.approval_status !== 'pending') return;
      if (!l.from_date || !l.from_date.startsWith(String(currentYear))) return;
      if (!(l.leave_type in acc)) return;
      let d = l.days_requested;
      if (d === undefined || d === null) {
        try {
          const f = new Date(l.from_date);
          const t = new Date(l.to_date);
          const span = Math.floor((t - f) / (1000 * 60 * 60 * 24)) + 1;
          d = l.leave_category === 'half_day' ? span * 0.5 : span;
        } catch (e) {
          d = l.leave_category === 'half_day' ? 0.5 : 1;
        }
      }
      acc[l.leave_type] += Number(d) || 0;
    });
    return acc;
  }, [leaves]);

  const leaveTypeLabelFull = {
    AL: 'Annual Leave',
    OH: 'Organization Holiday',
    RH: 'Restricted Holiday',
    LWP: 'Leave Without Pay',
  };

  const quotaWarning = useMemo(() => {
    if (!balance) return null;
    if (!['AL', 'OH', 'RH'].includes(leaveForm.leave_type)) return null;
    const available = balance?.[leaveForm.leave_type]?.available ?? 0;
    const pending = pendingByType[leaveForm.leave_type] || 0;
    const remaining = available - pending;
    if (calculatedDays === null) {
      // Nothing left at all — warn even before dates chosen
      if (remaining <= 0) {
        return {
          blocking: true,
          message: `You have already consumed your ${leaveTypeLabelFull[leaveForm.leave_type]} quota for the year (available: ${available}, pending approval: ${pending}). You cannot submit any more ${leaveForm.leave_type} requests.`,
        };
      }
      return null;
    }
    if (calculatedDays > remaining) {
      return {
        blocking: true,
        message: `Cannot apply ${calculatedDays} day(s) of ${leaveTypeLabelFull[leaveForm.leave_type]}. You have ${available} day(s) available and ${pending} day(s) already pending approval, leaving only ${remaining} day(s) you can still request this year.`,
      };
    }
    return null;
  }, [balance, leaveForm.leave_type, calculatedDays, pendingByType]);

  useEffect(() => {
    fetchData();
    if (user.role === 'manager' || user.role === 'admin') {
      fetchPendingApprovals();
    }
  }, []);

  // Filtered leaves based on filters
  const filteredLeaves = useMemo(() => {
    return leaves
      .filter(l => leaveTypeFilter === 'all' || l.leave_type === leaveTypeFilter)
      .filter(l => leaveStatusFilter === 'all' || l.approval_status === leaveStatusFilter)
      .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
  }, [leaves, leaveTypeFilter, leaveStatusFilter]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [balanceRes, leavesRes] = await Promise.all([
        axios.get(`${API}/leaves/balance`, config),
        axios.get(`${API}/leaves`, config)
      ]);

      setBalance(balanceRes.data);
      setLeaves(leavesRes.data);
    } catch (error) {
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/leaves/pending`, config);
      setPendingApprovals(response.data);
    } catch (error) {
      console.error('Failed to load pending approvals');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (quotaWarning?.blocking) {
      toast.error(quotaWarning.message);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/leaves`, leaveForm, config);
      toast.success('Leave application submitted');
      setShowDialog(false);
      setLeaveForm({
        leave_type: 'AL',
        leave_category: 'full_day',
        from_date: '',
        to_date: '',
        reason: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave application');
    }
  };

  // Open the Apply Leave dialog pre-filled with a previously-rejected request
  // so the employee can tweak fields (different leave type / dates / reason)
  // and submit again.
  const handleResubmit = (leave) => {
    setLeaveForm({
      leave_type: leave.leave_type || 'AL',
      leave_category: leave.leave_category || 'full_day',
      from_date: leave.from_date || '',
      to_date: leave.to_date || '',
      reason: leave.reason || '',
    });
    setShowDialog(true);
  };

  // Cancel a pending leave (the AlertDialog confirmation gates this call).
  // After success we refresh the entire leave list + balance so quota
  // numbers and the History table both reflect the freed-up days.
  const handleCancelLeave = async () => {
    if (!leaveToCancel) return;
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/leaves/${leaveToCancel.id}/cancel`, {}, config);
      toast.success('Leave cancelled');
      setLeaveToCancel(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel leave');
    } finally {
      setCancelling(false);
    }
  };


  const handleApproval = async (leaveId, approvalStatus) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/leaves/approve`, { leave_id: leaveId, approval_status: approvalStatus }, config);
      toast.success(`Leave ${approvalStatus}`);
      fetchPendingApprovals();
    } catch (error) {
      toast.error('Failed to process approval');
    }
  };

  const leaveTypeLabels = {
    AL: 'Annual Leave',
    OH: 'Organization Holiday',
    RH: 'Restricted Holiday',
    LWP: 'Leave Without Pay'
  };

  const getStatusBadge = (status) => {
    const badges = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-200 text-gray-700',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50" data-testid="leave-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto w-full">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Leave Management</h1>
            <p className="text-gray-600">Apply and track your leaves</p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700" data-testid="apply-leave-button">
                <Plus className="w-4 h-4 mr-2" />
                Apply Leave
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Apply for Leave</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="leave-form">
                <div>
                  <Label>Leave Type</Label>
                  <Select value={leaveForm.leave_type} onValueChange={(value) => setLeaveForm({ ...leaveForm, leave_type: value })}>
                    <SelectTrigger data-testid="leave-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AL">Annual Leave (AL)</SelectItem>
                      <SelectItem value="OH">Organization Holiday (OH)</SelectItem>
                      <SelectItem value="RH">Restricted Holiday (RH)</SelectItem>
                      <SelectItem value="LWP">Leave Without Pay (LWP)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Leave Category</Label>
                  <Select value={leaveForm.leave_category} onValueChange={(value) => setLeaveForm({ ...leaveForm, leave_category: value })}>
                    <SelectTrigger data-testid="leave-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_day">Full Day</SelectItem>
                      <SelectItem value="half_day">Half Day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={leaveForm.from_date}
                    onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })}
                    required
                    data-testid="from-date-input"
                  />
                </div>
                <div>
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={leaveForm.to_date}
                    onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })}
                    required
                    data-testid="to-date-input"
                  />
                </div>
                {calculatedDays !== null && (
                  <div className="p-3 bg-blue-50 rounded-lg text-center" data-testid="calculated-days">
                    <span className="text-sm text-gray-600">Number of Days: </span>
                    <span className="text-lg font-bold text-blue-700">{calculatedDays}</span>
                    <span className="text-sm text-gray-500 ml-1">
                      {leaveForm.leave_category === 'half_day' ? '(half day)' : `day${calculatedDays > 1 ? 's' : ''}`}
                    </span>
                  </div>
                )}
                {quotaWarning && (
                  <div
                    className="p-3 rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm"
                    data-testid="leave-quota-warning"
                    role="alert"
                  >
                    <p className="font-semibold mb-1">Leave quota exceeded</p>
                    <p>{quotaWarning.message}</p>
                  </div>
                )}
                <div>
                  <Label>Reason</Label>
                  <Textarea
                    value={leaveForm.reason}
                    onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                    required
                    rows={3}
                    data-testid="reason-textarea"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  data-testid="submit-leave-button"
                  disabled={Boolean(quotaWarning?.blocking)}
                >
                  Submit Application
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Manager Approvals */}
        {(user.role === 'manager' || user.role === 'admin') && pendingApprovals.length > 0 && (
          <Card className="mb-6" data-testid="pending-leave-approvals-section">
            <CardHeader>
              <CardTitle>Pending Leave Approvals ({pendingApprovals.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingApprovals.map(leave => (
                  <div key={leave.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`leave-approval-item-${leave.id}`}>
                    <div>
                      <p className="font-medium">{leave.employee_name}</p>
                      <p className="text-sm text-gray-600">
                        {leaveTypeLabels[leave.leave_type]} ({leave.leave_category})
                      </p>
                      <p className="text-sm text-gray-600">
                        {leave.from_date} to {leave.to_date}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">{leave.reason}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproval(leave.id, 'approved')} className="bg-green-600 hover:bg-green-700" data-testid={`approve-leave-${leave.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleApproval(leave.id, 'rejected')} className="border-red-600 text-red-600 hover:bg-red-50" data-testid={`reject-leave-${leave.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Leave Balance */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Leave Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg" data-testid="al-balance">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Annual Leave</h3>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Available</span>
                      <span className="font-semibold" data-testid="al-available-value">{balance?.AL?.available ?? 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Used</span>
                      <span className="font-semibold" data-testid="al-used-value">{balance?.AL?.used ?? 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total</span>
                      <span className="font-semibold" data-testid="al-total-value">{balance?.AL?.total ?? 0}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-green-50 rounded-lg" data-testid="oh-balance">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Organization Holiday</h3>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Available</span>
                      <span className="font-semibold" data-testid="oh-available-value">{balance?.OH?.available ?? 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Used</span>
                      <span className="font-semibold" data-testid="oh-used-value">{balance?.OH?.used ?? 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total</span>
                      <span className="font-semibold" data-testid="oh-total-value">{balance?.OH?.total ?? 0}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg" data-testid="rh-balance">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Restricted Holiday</h3>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Available</span>
                      <span className="font-semibold" data-testid="rh-available-value">{balance?.RH?.available ?? 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Used</span>
                      <span className="font-semibold" data-testid="rh-used-value">{balance?.RH?.used ?? 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total</span>
                      <span className="font-semibold" data-testid="rh-total-value">{balance?.RH?.total ?? 0}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-red-50 rounded-lg" data-testid="lwp-balance">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Leave Without Pay</h3>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Used</span>
                      <span className="font-semibold" data-testid="lwp-used-value">{balance?.LWP?.used ?? 0}</span>
                    </div>
                  </div>
                </div>

                {balance?.leave_encashment > 0 && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="leave-encashment">
                    <h3 className="text-sm font-medium text-amber-700 mb-2">Leave Encashment</h3>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Unused AL ({new Date().getFullYear() - 1})</span>
                        <span className="flex items-center gap-2">
                          <span className="font-semibold text-amber-700" data-testid="encashment-value">{balance.leave_encashment}</span>
                          {new Date().getMonth() >= 2 && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700" data-testid="encashment-processed-tag">Processed</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Leave History */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle>Leave History</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-3 mb-4 p-3 bg-gray-50 rounded-lg flex-wrap">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Filters:</span>
                </div>
                <Select value={leaveTypeFilter} onValueChange={setLeaveTypeFilter}>
                  <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="leave-type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="AL">Annual Leave</SelectItem>
                    <SelectItem value="SL">Sick Leave</SelectItem>
                    <SelectItem value="ML">Maternity Leave</SelectItem>
                    <SelectItem value="PL">Paternity Leave</SelectItem>
                    <SelectItem value="CL">Casual Leave</SelectItem>
                    <SelectItem value="OH">Organization Holiday</SelectItem>
                    <SelectItem value="RH">Restricted Holiday</SelectItem>
                    <SelectItem value="LWP">Leave Without Pay</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={leaveStatusFilter} onValueChange={setLeaveStatusFilter}>
                  <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="leave-status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                {(leaveTypeFilter !== 'all' || leaveStatusFilter !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setLeaveTypeFilter('all'); setLeaveStatusFilter('all'); }}
                    className="text-xs"
                  >
                    Clear
                  </Button>
                )}
                <span className="ml-auto text-sm text-gray-500">
                  Showing {filteredLeaves.length} of {leaves.length} records
                </span>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>From Date</TableHead>
                    <TableHead>To Date</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeaves.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-gray-500">
                        {leaves.length === 0 ? 'No leave applications found' : 'No matching records found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLeaves.map(leave => (
                      <TableRow key={leave.id} data-testid={`leave-row-${leave.id}`}>
                        <TableCell>{leaveTypeLabels[leave.leave_type]}</TableCell>
                        <TableCell className="capitalize">{leave.leave_category.replace('_', ' ')}</TableCell>
                        <TableCell>{leave.from_date}</TableCell>
                        <TableCell>{leave.to_date}</TableCell>
                        <TableCell className="max-w-xs truncate">{leave.reason}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(leave.approval_status)}`} data-testid={`leave-status-${leave.id}`}>
                            {leave.approval_status}
                          </span>
                        </TableCell>
                        <TableCell>{format(new Date(leave.submitted_at), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>
                          {leave.approval_status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-700 border-red-600 hover:bg-red-50 w-fit"
                              onClick={() => setLeaveToCancel(leave)}
                              data-testid={`cancel-leave-${leave.id}`}
                            >
                              Cancel
                            </Button>
                          )}
                          {leave.approval_status === 'rejected' && (
                            <div className="flex flex-col gap-1 items-start">
                              {leave.rejection_reason && (
                                <div className="text-xs text-red-700 italic max-w-[200px]" data-testid={`rejection-reason-${leave.id}`}>
                                  &quot;{leave.rejection_reason}&quot;
                                </div>
                              )}
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-700 border-blue-600 hover:bg-blue-50 w-fit"
                                  onClick={() => handleResubmit(leave)}
                                  data-testid={`resubmit-leave-${leave.id}`}
                                >
                                  Resubmit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-700 border-red-600 hover:bg-red-50 w-fit"
                                  onClick={() => setLeaveToCancel(leave)}
                                  data-testid={`cancel-leave-${leave.id}`}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog
        open={!!leaveToCancel}
        onOpenChange={(open) => { if (!open) setLeaveToCancel(null); }}
      >
        <AlertDialogContent data-testid="cancel-leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this leave request?</AlertDialogTitle>
            <AlertDialogDescription>
              {leaveToCancel ? (
                <>
                  This will withdraw your {leaveTypeLabels[leaveToCancel.leave_type] || leaveToCancel.leave_type} request for{' '}
                  <span className="font-medium">{leaveToCancel.from_date}</span>
                  {leaveToCancel.to_date && leaveToCancel.to_date !== leaveToCancel.from_date && (
                    <> &nbsp;→ &nbsp;<span className="font-medium">{leaveToCancel.to_date}</span></>
                  )}
                  . The days will be returned to your available balance. This action cannot be undone — you would have to submit a new request to re-apply.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-leave-dialog-keep" disabled={cancelling}>
              Keep leave
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelLeave}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
              data-testid="cancel-leave-dialog-confirm"
            >
              {cancelling ? 'Cancelling…' : 'Yes, cancel leave'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeavePage;
